// @ts-nocheck

import {
  KNOWLEDGE_BASE_ALLOWED_EXTENSIONS,
  KNOWLEDGE_BASE_INVALID_FILE_MESSAGE,
} from './constant';

export const BLANK_FILE_MESSAGE =
  'This document is empty. Please upload a file that has some content.';

const TEXT_EXTENSIONS = ['txt'];
const INVISIBLE_CHARS = /[\s\u200B-\u200D\uFEFF]/g;

async function inflate(bytes: Uint8Array, format: 'deflate' | 'deflate-raw'): Promise<Uint8Array> {
  const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream(format));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

const canInflate = () => typeof DecompressionStream !== 'undefined' && typeof Blob !== 'undefined';


async function readDocxDocumentXml(buf: ArrayBuffer): Promise<string | null> {
  const view = new DataView(buf);
  const bytes = new Uint8Array(buf);

  
  let eocd = -1;
  for (let i = bytes.length - 22; i >= Math.max(0, bytes.length - 22 - 65535); i--) {
    if (view.getUint32(i, true) === 0x06054b50) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) return null;

  const entryCount = view.getUint16(eocd + 10, true);
  let p = view.getUint32(eocd + 16, true);
  const decoder = new TextDecoder();

  for (let n = 0; n < entryCount; n++) {
    if (p + 46 > bytes.length || view.getUint32(p, true) !== 0x02014b50) return null;
    const method = view.getUint16(p + 10, true);
    const compSize = view.getUint32(p + 20, true);
    const nameLen = view.getUint16(p + 28, true);
    const extraLen = view.getUint16(p + 30, true);
    const commentLen = view.getUint16(p + 32, true);
    const localOffset = view.getUint32(p + 42, true);
    const name = decoder.decode(bytes.subarray(p + 46, p + 46 + nameLen));

    if (name === 'word/document.xml') {
      if (view.getUint32(localOffset, true) !== 0x04034b50) return null;
      const lNameLen = view.getUint16(localOffset + 26, true);
      const lExtraLen = view.getUint16(localOffset + 28, true);
      const start = localOffset + 30 + lNameLen + lExtraLen;
      const data = bytes.subarray(start, start + compSize);
      if (method === 0) return decoder.decode(data);
      if (method === 8) return decoder.decode(await inflate(data, 'deflate-raw'));
      return null;
    }
    p += 46 + nameLen + extraLen + commentLen;
  }
  return null;
}

async function isBlankDocx(file: File): Promise<boolean> {
  try {
    if (!canInflate()) return false;
    const xml = await readDocxDocumentXml(await file.arrayBuffer());
    if (xml === null) return false;
    if (/<w:(drawing|pict|object)\b|<mc:AlternateContent\b|<w:sdt\b/.test(xml)) return false;
    const text = Array.from(xml.matchAll(/<w:t(?:\s[^>]*)?>([^<]*)<\/w:t>/g))
      .map((m) => m[1])
      .join('');
    return !text.replace(INVISIBLE_CHARS, '');
  } catch {
    return false;
  }
}

async function isBlankPdf(file: File): Promise<boolean> {
  try {
    if (!canInflate()) return false;
    const bytes = new Uint8Array(await file.arrayBuffer());
    const raw = new TextDecoder('latin1').decode(bytes); 

    if (!/^%PDF-/.test(raw.slice(0, 1024))) return false;
    if (/\/Encrypt\b/.test(raw)) return false;
    if (/\/Subtype\s*\/Image\b/.test(raw)) return false;

    const TEXT_OP = /(\((?:[^()\\]|\\[\s\S])*\)|\[[^\]]*\]|<[0-9A-Fa-f\s]+>)\s*(?:Tj|TJ)\b/g;
    const LITERAL = /\((?:[^()\\]|\\[\s\S])*\)/g;
    const BG_RECT = /-?[\d.]+\s+-?[\d.]+\s+-?[\d.]+\s+-?[\d.]+\s+re\s+f\*?/g;
    const PAINT_OP = /(^|\s)(f\*?|F|S|s|B\*?|b\*?|sh|Do)(\s|$)/;
    const SKIP_DICT = /\/Type\s*\/(XRef|ObjStm|Metadata|EmbeddedFile)|\/Length1|\/FontFile|\/FunctionType|\/N\s+\d/;

    let inspected = 0;
    let cursor = 0;
    while (true) {
      const s = raw.indexOf('stream', cursor);
      if (s < 0) break;
      if (raw.startsWith('endstream', s - 3)) {
        cursor = s + 6;
        continue;
      }
      let dataStart = s + 6;
      if (raw[dataStart] === '\r') dataStart++;
      if (raw[dataStart] === '\n') dataStart++;
      const end = raw.indexOf('endstream', dataStart);
      if (end < 0) break;
      cursor = end + 9;

      const dictStart = Math.max(raw.lastIndexOf('obj', s), s - 1500);
      const dict = raw.slice(dictStart, s);
      if (SKIP_DICT.test(dict)) continue;
      if (/\/DCTDecode|\/JPXDecode|\/CCITTFaxDecode|\/JBIG2Decode/.test(dict)) return false;

      let data = bytes.subarray(dataStart, end);
      if (/\/Filter/.test(dict)) {
        if (!/\/FlateDecode|\/Fl\b/.test(dict) || /\/(LZW|ASCII85|ASCIIHex|RunLength)Decode/.test(dict)) return false;
       
        let inflated: Uint8Array | null = null;
        for (const trim of [2, 1, 0]) {
          try {
            inflated = await inflate(data.subarray(0, data.length - trim), 'deflate');
            break;
          } catch {
          }
        }
        if (!inflated) return false;
        data = inflated;
      }
      inspected++;
      const content = new TextDecoder('latin1').decode(data);

      if (PAINT_OP.test(content.replace(BG_RECT, ' '))) return false;

      TEXT_OP.lastIndex = 0;
      let m: RegExpExecArray | null;
      while ((m = TEXT_OP.exec(content))) {
        const operand = m[1] || '';
        if (/<[0-9A-Fa-f\s]+>/.test(operand)) return false;
        const literals = operand.match(LITERAL) || [];
        if (literals.some((l) => l.slice(1, -1).replace(INVISIBLE_CHARS, '').length > 0)) return false;
      }
    }
    if (inspected > 0) return true;
    return /\/Type\s*\/Page\b/.test(raw) && !/\/Contents\b/.test(raw) && !/\/ObjStm\b/.test(raw);
  } catch {
    return false;
  }
}

export async function validateKnowledgeBaseFile(file: File | undefined | null): Promise<string | null> {
  if (!file) return null;

  const dotIndex = file.name.lastIndexOf('.');
  const ext = dotIndex >= 0 ? file.name.slice(dotIndex + 1).toLowerCase() : '';
  if (!KNOWLEDGE_BASE_ALLOWED_EXTENSIONS.includes(ext)) return KNOWLEDGE_BASE_INVALID_FILE_MESSAGE;

  if (file.size === 0) return BLANK_FILE_MESSAGE;

  if (TEXT_EXTENSIONS.includes(ext)) {
    try {
      const text = await file.text();
      if (!text.replace(INVISIBLE_CHARS, '')) return BLANK_FILE_MESSAGE;
    } catch {
    }
  } else if (ext === 'docx') {
    if (await isBlankDocx(file)) return BLANK_FILE_MESSAGE;
  } else if (ext === 'pdf') {
    if (await isBlankPdf(file)) return BLANK_FILE_MESSAGE;
  }
  return null;
}
